import type { EmslUserType } from '@/types/auth';

export interface NavItem {
  url: string;
  caption: string;
  /** Matches the route name; used by the auth guard's nav-membership check. */
  pageSignature: string;
  /** PrimeIcons class (e.g. 'pi pi-shopping-cart'); shown in the collapsed ribbon. */
  icon?: string;
  hidden?: boolean;
  /**
   * Only shown when the user belongs to more than one organization. Mirrors the
   * legacy `<emsl-nav>` directive, which rendered the "Switch" control behind
   * `ng-show="organizations.length > 1"`.
   */
  requiresMultiOrg?: boolean;
}

const ClientNavigation: NavItem[] = [
  { url: '#/orders', caption: 'Orders', pageSignature: 'reservations', icon: 'pi pi-shopping-cart' },
  {
    url: '#/switchOrganization',
    caption: 'Switch Organization',
    pageSignature: 'switchOrganization',
    icon: 'pi pi-sync',
    requiresMultiOrg: true,
  },
  { url: '#/logout', caption: 'Sign Out', pageSignature: 'logout', icon: 'pi pi-sign-out' },
];

const RegularLabNavigation: NavItem[] = [
  { url: '#/labOrders', caption: 'Orders', pageSignature: 'labOrders', icon: 'pi pi-shopping-cart' },
  { url: '#/logout', caption: 'Sign Out', pageSignature: 'logout', icon: 'pi pi-sign-out' },
];

const AdminLabNavigation: NavItem[] = [
  { url: '#/labOrders', caption: 'Orders', pageSignature: 'labOrders', icon: 'pi pi-shopping-cart' },
  {
    url: '#/analysisTypes',
    caption: 'Analysis Types',
    pageSignature: 'analysisTypes',
    icon: 'pi pi-tags',
  },
  { url: '#/labs', caption: 'Labs', pageSignature: 'labs', icon: 'pi pi-building' },
  {
    url: '#/labAccreditations',
    caption: 'Expiration Dates',
    pageSignature: 'labAccreditations',
    icon: 'pi pi-calendar',
  },
  { url: '#/capacity', caption: 'Capacity', pageSignature: 'capacity', icon: 'pi pi-chart-bar' },
  // Stats (#/userstats) removed — no longer in use. The UserStatsView route still
  // exists in routes.ts but is no longer reachable from the nav.
  { url: '#/support', caption: 'Support', pageSignature: 'support', icon: 'pi pi-question-circle' },
  { url: '#/logout', caption: 'Sign Out', pageSignature: 'logout', icon: 'pi pi-sign-out' },
];

const SuperAdminNavigation: NavItem[] = [
  // -------------------------------------------------------------------------
  // DEPRECATED — Forms admin (Projects + Forms* routes) is no longer in use.
  // These hidden entries only exist to keep the Formyula Administrator's
  // forms-management routes reachable by the auth guard. Slated for removal
  // along with the Project/Form views and their routes; do not extend.
  // -------------------------------------------------------------------------
  { url: '#/projects', caption: 'Projects', pageSignature: 'projects', hidden: true },
  { url: '#/projects/1/forms', caption: 'Forms', pageSignature: 'projectForms', hidden: true },
  {
    url: '#/projects/1/forms',
    caption: 'FormsEdit',
    pageSignature: 'projectFormEdit',
    hidden: true,
  },
  {
    url: '#/projects/1/forms',
    caption: 'FormsAdd',
    pageSignature: 'projectFormsAdd',
    hidden: true,
  },
  {
    url: '#/projects/1/forms',
    caption: 'FormsAddData',
    pageSignature: 'projectData',
    hidden: true,
  },
  {
    url: '#/projects/1/forms',
    caption: 'FormsAddAnalyses',
    pageSignature: 'projectAnalyses',
    hidden: true,
  },
  {
    url: '#/projects/1/forms',
    caption: 'FormsAddAnalysisAdd',
    pageSignature: 'projectAnalysisAdd',
    hidden: true,
  },
  {
    url: '#/projects/1/forms',
    caption: 'FormsAddAnalysisEdit',
    pageSignature: 'projectAnalysisEdit',
    hidden: true,
  },
  {
    url: '#/projects/1/forms',
    caption: 'FormsAddSettings',
    pageSignature: 'projectSettings',
    hidden: true,
  },
  // ---- end deprecated forms-admin block ----
  { url: '#/labOrders', caption: 'Orders', pageSignature: 'labOrders', icon: 'pi pi-shopping-cart' },
  {
    url: '#/analysisTypes',
    caption: 'Analysis Types',
    pageSignature: 'analysisTypes',
    icon: 'pi pi-tags',
  },
  { url: '#/labs', caption: 'Labs', pageSignature: 'labs', icon: 'pi pi-building' },
  {
    url: '#/labAccreditations',
    caption: 'Expiration Dates',
    pageSignature: 'labAccreditations',
    icon: 'pi pi-calendar',
  },
  { url: '#/capacity', caption: 'Capacity', pageSignature: 'capacity', icon: 'pi pi-chart-bar' },
  // Stats (#/userstats) removed — no longer in use.
  { url: '#/support', caption: 'Support', pageSignature: 'support', icon: 'pi pi-question-circle' },
  { url: '#/logout', caption: 'Sign Out', pageSignature: 'logout', icon: 'pi pi-sign-out' },
];

export const NAV_BY_USER_TYPE: Record<string, NavItem[]> = {
  'Formyula Administrator': SuperAdminNavigation,
  Administrator: AdminLabNavigation,
  Employee: RegularLabNavigation,
  'Client Administrator': ClientNavigation,
  'Client User': ClientNavigation,
};

export function navFor(userType: EmslUserType | null | undefined): NavItem[] {
  if (!userType) return [];
  return NAV_BY_USER_TYPE[userType] ?? [];
}

/** Route names that don't require authentication. */
export const PUBLIC_ROUTE_NAMES = new Set<string>([
  'main',
  'register',
  'acceptInvite',
  'forgotPassword',
  'resetPassword',
  'remoteLinkLogIn',
]);
